import { useMemo, useState } from 'react';

import { canMutate } from '@domain/auth/AdminUser';
import { useAuth } from '@app/hooks/useAuth';
import { ADMIN_API_BASE, apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { errorText } from '@infrastructure/api/errors';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';

// talky.ca/contact form submissions (backend: enquiries app)
export interface EnquiryRow {
  id: string;
  created_at: string;
  topic: string;
  topic_label: string;
  name: string;
  email: string;
  company: string;
  monthly_volume: string;
  message: string;
  source_page: string;
  status: 'new' | 'contacted' | 'closed';
  staff_note: string;
  handled_by: string;
  handled_at: string | null;
  staff_notified_at: string | null;
  auto_reply_sent_at: string | null;
}

const TOPICS: [string, string][] = [
  ['sales', 'Sales'], ['wholesale', 'Wholesale SMPP'], ['marketing', 'Marketing & partnerships'],
  ['support', 'Support'], ['other', 'Other'],
];
const STATUSES: [EnquiryRow['status'], string][] = [['new', 'New'], ['contacted', 'Contacted'], ['closed', 'Closed']];
const STATUS_STYLE: Record<EnquiryRow['status'], string> = {
  new: 'bg-blue-100 text-blue-800', contacted: 'bg-amber-100 text-amber-800', closed: 'bg-slate-100 text-slate-600',
};

const repo = new AdminListRepository<EnquiryRow>(apiClient, 'enquiries');

const columns: DataTableColumn<EnquiryRow>[] = [
  { id: 'created_at', header: 'Received', accessor: (r) => formatDateTime(r.created_at) },
  {
    id: 'status', header: 'Status',
    accessor: (r) => <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[r.status]}`}>{r.status}</span>,
  },
  { id: 'topic', header: 'Topic', accessor: 'topic_label' },
  {
    id: 'name', header: 'From',
    accessor: (r) => (
      <span>
        {r.name}
        {r.company && <span className="block text-xs text-slate-500">{r.company}</span>}
      </span>
    ),
  },
  { id: 'email', header: 'Email', accessor: 'email' },
  {
    id: 'message', header: 'Message',
    accessor: (r) => <span className="block max-w-[22rem] truncate" title={r.message}>{r.message}</span>,
  },
];

function EnquiryDetail({ row, canEdit, onSaved, onClose }: {
  row: EnquiryRow; canEdit: boolean; onSaved: () => void; onClose: () => void;
}) {
  const [status, setStatus] = useState(row.status);
  const [note, setNote] = useState(row.staff_note);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    setSaving(true);
    setError('');
    try {
      await apiClient.patch(`${ADMIN_API_BASE}/enquiries/${row.id}/`, { status, staff_note: note });
      onSaved();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  const subject = encodeURIComponent(`Re: your message to Talky (${row.topic_label})`);
  return (
    <div className="mb-4 rounded border bg-white p-4 text-sm shadow-sm dark:bg-slate-900" data-testid="enquiry-detail">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold">{row.topic_label}</h3>
          <p className="text-slate-500">{formatDateTime(row.created_at)}{row.source_page && ` · from ${row.source_page}`}</p>
        </div>
        <button type="button" className="rounded border px-2 py-0.5 text-xs" onClick={onClose}>Close</button>
      </div>
      <dl className="mb-3 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1">
        <dt className="text-slate-500">Name</dt><dd>{row.name}</dd>
        <dt className="text-slate-500">Email</dt>
        <dd><a className="text-blue-600 underline" href={`mailto:${row.email}?subject=${subject}`}>{row.email}</a></dd>
        <dt className="text-slate-500">Company</dt><dd>{row.company || '—'}</dd>
        <dt className="text-slate-500">Monthly volume</dt><dd>{row.monthly_volume || '—'}</dd>
        <dt className="text-slate-500">Emails</dt>
        <dd>
          {row.staff_notified_at ? 'Sent to team' : 'Not sent to team yet'}
          {' · '}
          {row.auto_reply_sent_at ? 'confirmation sent' : 'no confirmation'}
        </dd>
        {row.handled_by && (<><dt className="text-slate-500">Last change</dt>
          <dd>{row.handled_by}{row.handled_at && `, ${formatDateTime(row.handled_at)}`}</dd></>)}
      </dl>
      <p className="mb-3 whitespace-pre-wrap rounded bg-slate-50 p-3 dark:bg-slate-800">{row.message}</p>
      {canEdit ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Status</span>
            <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as EnquiryRow['status'])}
              className="rounded border px-2 py-1">
              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
            <span className="text-xs text-slate-500">Note (team only)</span>
            <textarea aria-label="Note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              className="rounded border px-2 py-1" />
          </label>
          <button type="button" disabled={saving} onClick={save}
            className="rounded bg-blue-600 px-3 py-1.5 text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          {error && <p className="w-full text-red-600" role="alert">{error}</p>}
        </div>
      ) : (
        <p className="text-slate-500">Status: {row.status}{row.staff_note && ` · Note: ${row.staff_note}`}</p>
      )}
    </div>
  );
}

export function EnquiriesPage() {
  const user = useAuth((st) => st.user);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [topic, setTopic] = useState('');
  const [selected, setSelected] = useState<EnquiryRow | null>(null);
  const [reload, setReload] = useState(0);
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (status) f.status = status;
    if (topic) f.topic = topic;
    return f;
  }, [search, status, topic]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Enquiries</h2>
          <p className="text-sm text-slate-500">Messages from the talky.ca contact form.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select aria-label="Filter status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded border px-2 py-1">
            <option value="">All statuses</option>
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select aria-label="Filter topic" value={topic} onChange={(e) => setTopic(e.target.value)}
            className="rounded border px-2 py-1">
            <option value="">All topics</option>
            {TOPICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="search" placeholder="Search name, email, company, message" value={search}
            onChange={(e) => setSearch(e.target.value)} className="rounded border px-2 py-1" />
        </div>
      </div>
      {selected && (
        <EnquiryDetail
          key={selected.id}
          row={selected}
          canEdit={canMutate(user)}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); setReload((n) => n + 1); }}
        />
      )}
      <DataTable<EnquiryRow>
        key={reload}
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        onRowClick={(r) => setSelected(r)}
        emptyState={<span data-testid="datatable-empty">No enquiries yet.</span>}
      />
    </section>
  );
}
